import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Card, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import UserDataForm from '../components/UserDataForm';
import FaceScanIframe from '../components/FaceScanIframe';
import ScanResults from '../components/ScanResults';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import {
  UserDemographics,
  FaceScanModuleData,
  SkinScanData,
  FaceScanAPIRequest,
  SkinScanAPIRequest,
  ScanStep,
} from '../types';

/**
 * API CONFIGURATION:
 * ==================
 * 
 * TESTING MODE: Currently using FaceSense production APIs directly
 * PRODUCTION MODE: Switch to n8n webhook (commented below)
 * 
 * TO SWITCH TO N8N WEBHOOK:
 * 1. Comment out FACESCAN_API_URL and SKINSCAN_API_URL
 * 2. Uncomment WEBHOOK_URL
 * 3. Update fetch calls to use WEBHOOK_URL with { type, data } wrapper
 * 4. Change 'x-api-key' to 'X-API-Key' in headers
 */

// FaceSense Production APIs (Current - for testing)
const FACESCAN_API_URL = 'https://prd.server.watchyourhealth.com/apis/s/face-vital-scan';
const SKINSCAN_API_URL = 'https://prd.server.watchyourhealth.com/apis/s/skin-scan';

// Your n8n Webhook (Uncomment for production)
// const WEBHOOK_URL = 'https://etl.futurisk.in/webhook/fee3ecb4-f2bb-4955-8f29-7bb4715cd6c3';

const API_KEY = 'ra-CgCkaCR2gX4EeNRVarnVz5kVvNWqN05f';

const createScanSessionUid = () => `open_user_${Date.now()}`;

const OpenUserFaceScanPage: React.FC = () => {
  const location = useLocation();
  
  // Extract crmclientid from URL query params (optional parameter)
  const crmClientId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('crmclientid') || null;
  }, [location.search]);

  const [currentStep, setCurrentStep] = useState<ScanStep>(ScanStep.USER_INFO);
  const [userData, setUserData] = useState<UserDemographics | null>(null);
  const [faceScanData, setFaceScanData] = useState<FaceScanModuleData | null>(null);
  const [skinScanData, setSkinScanData] = useState<SkinScanData | null>(null);
  const [scanResults, setScanResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [scanUid, setScanUid] = useState<string>(() => createScanSessionUid());

  const showMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleUserDataSubmit = (data: UserDemographics) => {
    setScanUid(createScanSessionUid());
    setUserData(data);
    setCurrentStep(ScanStep.SCANNING);
  };

  const handleScanComplete = async (scanData: FaceScanModuleData) => {
    // console.log('=== SCAN COMPLETE EVENT RECEIVED ===');
    // console.log('Scan Data:', scanData);
    // console.log('Current Step:', currentStep);
    setFaceScanData(scanData);
    // console.log('Face scan data set in state');
  };

  const handleSkinScanComplete = (skinData: SkinScanData) => {
    // console.log('=== SKIN SCAN DATA RECEIVED ===');
    // console.log('Skin Data:', skinData);
    setSkinScanData(skinData);
    // console.log('Skin scan data set in state');
  };

  // When both scans are complete, submit to API
  React.useEffect(() => {
    // console.log('=== useEffect Triggered ===');
    // console.log('faceScanData:', faceScanData ? 'Present' : 'Null');
    // console.log('userData:', userData ? 'Present' : 'Null');
    // console.log('currentStep:', currentStep);
    // console.log('skinScanData:', skinScanData ? 'Present' : 'Null');
    
    const submitData = async () => {
      // console.log('=== Starting submitData function ===');
      
      if (!userData || !faceScanData) {
        // console.log('Missing required data - aborting');
        return;
      }

      // console.log('Setting step to PROCESSING...');
      setCurrentStep(ScanStep.PROCESSING);

      try {
        // Prepare Face Scan API request
        const faceScanRequest: FaceScanAPIRequest = {
          ...faceScanData,
          ...userData,
          uid: scanUid,
          org_name: 'futurisk_wellness',
          user_name: userData.name,
          phone: '+919999999999',
        };

        // console.log('=== FACE SCAN SUBMISSION ===');
        // console.log('API URL:', FACESCAN_API_URL);
        // console.log('Request Data:', faceScanRequest);

        // Submit to FaceSense Production API
        const faceScanResponse = await fetch(FACESCAN_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
          },
          body: JSON.stringify(faceScanRequest),
        });

        // console.log('Face Scan Response Status:', faceScanResponse.status);
        // console.log('Face Scan Response OK:', faceScanResponse.ok);

        if (!faceScanResponse.ok) {
          const errorText = await faceScanResponse.text();
          // console.error('Face Scan API Error Response:', errorText);
          throw new Error(`Face scan API failed (${faceScanResponse.status}): ${errorText}`);
        }

        const responseText = await faceScanResponse.text();
        // console.log('Face Scan Raw Response:', responseText);
        
        let faceScanResult;
        try {
          faceScanResult = JSON.parse(responseText);
          // console.log('Face Scan Parsed Response:', faceScanResult);
        } catch (parseError) {
          // console.error('Failed to parse response:', parseError);
          throw new Error('Invalid response format from API');
        }
        
        // console.log('=== FACE SCAN COMPLETE ===');

        // If we have skin scan data, submit it too
        let skinScanResult = null;
        if (skinScanData) {
          // console.log('=== SKIN SCAN SUBMISSION ===');
          // console.log('Skin scan data available:', !!skinScanData);
          // console.log('Image data length:', skinScanData.image?.length);
          // console.log('API URL:', SKINSCAN_API_URL);
          
          const skinScanRequest: SkinScanAPIRequest = {
            image_base64: skinScanData.image,
            org_name: 'futurisk_wellness',
          };

          // console.log('Submitting skin scan request...');
          
          // Submit to FaceSense Production API
          const skinScanResponse = await fetch(SKINSCAN_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'x-api-key': API_KEY,
            },
            body: JSON.stringify(skinScanRequest),
          });

          // console.log('Skin Scan Response Status:', skinScanResponse.status);
          // console.log('Skin Scan Response OK:', skinScanResponse.ok);

          if (skinScanResponse.ok) {
            const skinResponseText = await skinScanResponse.text();
            // console.log('Skin Scan Raw Response:', skinResponseText);
            
            try {
              skinScanResult = JSON.parse(skinResponseText);
              // console.log('Skin Scan Parsed Response:', skinScanResult);
              // console.log('=== SKIN SCAN COMPLETE ===');
            } catch (parseError) {
              // console.error('Failed to parse skin scan response:', parseError);
            }
          } else {
            const errorText = await skinScanResponse.text();
            // console.error('Skin Scan API Error Response:', errorText);
          }
        } else {
          // console.log('=== NO SKIN SCAN DATA ===');
          // console.log('Skin scan was not performed or data not received');
        }

        // Combine results
        // console.log('=== COMBINING RESULTS ===');
        // console.log('Face Scan Result:', faceScanResult);
        // console.log('Skin Scan Result:', skinScanResult);
        
        const combinedResults = {
          ...faceScanResult,
          skinScan: skinScanResult,
        };

        // console.log('Combined Results:', combinedResults);
        
        // Log to webhook before displaying results
        try {
          // console.log('=== LOGGING TO WEBHOOK ===');
          const loggingPayload = {
            userData: {
              ...userData,
              crmClientId: crmClientId,
            },
            faceScanResult: faceScanResult,
            skinScanResult: skinScanResult,
            timestamp: new Date().toISOString(),
            scanUid: scanUid,
          };
          
          const loggingResponse = await fetch('https://etl.futurisk.in/webhook/f3313cc4-3649-4a4b-a9e4-eb28c7581d5c', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(loggingPayload),
          });
          
          // console.log('Logging Webhook Response Status:', loggingResponse.status);
          if (loggingResponse.ok) {
            // console.log('Successfully logged to webhook');
          } else {
            // console.warn('Webhook logging failed but continuing:', loggingResponse.status);
          }
        } catch (loggingError) {
          // Don't fail the scan if logging fails
          // console.warn('Webhook logging error (non-critical):', loggingError);
        }
        
        // console.log('Setting results and navigating to RESULTS step...');
        
        setScanResults(combinedResults);
        setCurrentStep(ScanStep.RESULTS);
        showMessage('Scan completed successfully!');
        
        // console.log('=== SUBMISSION COMPLETE ===');
      } catch (err: any) {
        // console.error('=== ERROR OCCURRED ===');
        // console.error('Error Type:', err.name);
        // console.error('Error Message:', err.message);
        // console.error('Error Stack:', err.stack);
        // console.error('Full Error:', err);
        
        setError(err.message || 'Failed to process scan data');
        setCurrentStep(ScanStep.ERROR);
        showMessage('Failed to process scan data. Please try again.');
      }
    };

    if (faceScanData && userData && currentStep === ScanStep.SCANNING) {
      // console.log('=== Conditions met - calling submitData ===');
      submitData();
    } else {
      // console.log('=== Conditions NOT met - not submitting ===');
    }
  }, [faceScanData, userData, currentStep, skinScanData, scanUid, crmClientId]);

  const handleScanError = (errorMsg: string) => {
    // console.error('Scan error:', errorMsg);
    setError(errorMsg);
    setCurrentStep(ScanStep.ERROR);
    showMessage(errorMsg);
  };

  const handleNewScan = () => {
    setCurrentStep(ScanStep.USER_INFO);
    setUserData(null);
    setFaceScanData(null);
    setSkinScanData(null);
    setScanResults(null);
    setError(null);
  };

  const handleCancelScan = () => {
    setCurrentStep(ScanStep.USER_INFO);
    setFaceScanData(null);
    setSkinScanData(null);
  };

  const handleDownloadReport = () => {
    if (!scanResults) return;

    const dataStr = JSON.stringify(scanResults, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `face-scan-report-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showMessage('Report downloaded successfully!');
  };

  return (
    <div className="min-vh-100 bg-light py-5">
      <Container style={{ maxWidth: '800px' }}>
        {/* Step 1: User Information Form */}
        {currentStep === ScanStep.USER_INFO && (
          <UserDataForm onSubmit={handleUserDataSubmit} />
        )}

        {/* Step 2: Face Scanning */}
        {currentStep === ScanStep.SCANNING && userData ? (
          <FaceScanIframe
            userData={userData}
            scanUid={scanUid}
            onScanComplete={handleScanComplete}
            onSkinScanComplete={handleSkinScanComplete}
            onError={handleScanError}
            onCancel={handleCancelScan}
          />
        ) : null}

        {/* Step 3: Processing */}
        {currentStep === ScanStep.PROCESSING && (
          <Card className="border-0 shadow-lg">
            <Card.Body className="text-center py-5">
              <div className="mb-4">
                <Spinner 
                  animation="border" 
                  variant="primary" 
                  style={{ width: '5rem', height: '5rem', borderWidth: '0.4rem' }} 
                />
              </div>
              <h2 className="mb-3 d-flex align-items-center justify-content-center">
                <span className="svg-icon svg-icon-2hx svg-icon-primary me-3">
                  <KTSVG path='/media/icons/duotune/technology/teh004.svg' className='svg-icon-2hx' />
                </span>
                Analyzing Your Face
              </h2>
              <p className="text-muted fs-5 mb-4">
                Our AI is processing your vital signs...
              </p>
              
              {/* Analysis Steps Animation */}
              <div className="mt-4">
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <div className="badge bg-primary-subtle text-primary fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/medicine/med001.svg' className='svg-icon-3 me-2' />
                    Heart Rate
                  </div>
                  <div className="badge bg-success-subtle text-success fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/medicine/med004.svg' className='svg-icon-3 me-2' />
                    Blood Oxygen
                  </div>
                  <div className="badge bg-danger-subtle text-danger fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/medicine/med002.svg' className='svg-icon-3 me-2' />
                    Blood Pressure
                  </div>
                  <div className="badge bg-info-subtle text-info fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/medicine/med003.svg' className='svg-icon-3 me-2' />
                    Breathing Rate
                  </div>
                  {skinScanData && (
                    <div className="badge bg-warning-subtle text-warning fs-6 p-3">
                      <KTSVG path='/media/icons/duotune/general/gen049.svg' className='svg-icon-3 me-2' />
                      Skin Analysis
                    </div>
                  )}
                </div>
              </div>
              
              <p className="text-muted mt-4 mb-0">
                <small>This may take a few moments...</small>
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Step 4: Results */}
        {currentStep === ScanStep.RESULTS && scanResults && (
          <ScanResults
            data={scanResults}            userData={userData}            onNewScan={handleNewScan}
            onDownload={handleDownloadReport}
          />
        )}

        {/* Step 5: Error */}
        {currentStep === ScanStep.ERROR && (
          <Card bg="danger" text="white">
            <Card.Body>
              <h3>Scan Failed</h3>
              <p>{error || 'An unexpected error occurred. Please try again.'}</p>
              <button
                onClick={handleNewScan}
                className="btn btn-light mt-3"
              >
                Try Again
              </button>
            </Card.Body>
          </Card>
        )}
      </Container>

      <ToastContainer position="bottom-center" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg="dark"
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default OpenUserFaceScanPage;
